import { useCallback, useMemo, useRef, useState } from 'react';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext/AuthContext';
import { LISTING_LEASE_VERSION } from '../../constants/listingLease';
import { getListingAddress, getListingName } from '../../pages/MyProperties/listingDisplay';
import LeaseAgreementGate from './LeaseAgreementGate';

function formatAddress(addr) {
    if (!addr) return '—';
    const city = addr.city || '';
    const state = addr.state || '';
    const cityState = city && state ? `${city}, ${state}` : city || state;
    return [addr.street1, addr.street2, cityState, addr.zip].filter(Boolean).join(', ') || '—';
}

export function listingToGateItem(listing) {
    return {
        _id: listing._id,
        name: listing.name ?? getListingName(listing),
        addr: listing.addr ?? getListingAddress(listing),
    };
}

export function needsLeaseAcceptance(listings) {
    return listings.some((listing) => !listing.leaseAcceptedAt);
}

async function submitListings(listings, { getSubmitBody, includeLeaseVersion, signerName }) {
    await Promise.all(
        listings.map(async (listing) => {
            const body = getSubmitBody ? getSubmitBody(listing) : {};
            if (includeLeaseVersion || needsLeaseAcceptance([listing])) {
                body.leaseVersion = LISTING_LEASE_VERSION;
                body.signerName = signerName;
            }
            return axios.post(`/listings/${listing._id}/submit`, body);
        }),
    );
}

/**
 * Gate property submissions that have not yet accepted the lease.
 * Returns { runWithLeaseGate, gateProps } — render <LeaseAgreementGate {...gateProps} />.
 */
export default function useLeaseGate() {
    const { user } = useAuth();
    const pendingRef = useRef(null);
    const [gateState, setGateState] = useState({
        open: false,
        listings: [],
        busy: false,
    });
    const [hostData, setHostData] = useState(null);

    const closeGate = useCallback(() => {
        pendingRef.current = null;
        setGateState({ open: false, listings: [], busy: false });
        setHostData(null);
    }, []);

    const runWithLeaseGate = useCallback(async ({
        listings,
        getSubmitBody,
        onSuccess,
        onError,
    }) => {
        if (!listings?.length) return;

        if (!needsLeaseAcceptance(listings)) {
            try {
                await submitListings(listings, { getSubmitBody, includeLeaseVersion: false });
                await onSuccess?.();
            } catch (err) {
                await onError?.(err);
                throw err;
            }
            return;
        }

        // Fetch host profile data before opening the modal so section 1.2 is pre-populated.
        const [settingsResp, companyResp] = await Promise.all([
            axios.get('/profile/settings').catch(() => ({ data: null })),
            axios.get('/profile/company-settings').catch(() => ({ data: null })),
        ]);
        setHostData({
            settings: settingsResp.data?.profile?.basicDetails ?? null,
            company: companyResp.data?.company ?? null,
            openedAt: new Date().toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
            }),
        });

        return new Promise((resolve) => {
            pendingRef.current = {
                listings,
                getSubmitBody,
                onSuccess,
                onError,
                resolve,
            };
            setGateState({
                open: true,
                listings: listings.map(listingToGateItem),
                busy: false,
            });
        });
    }, []);

    const handleAccept = useCallback(async (signerName) => {
        const pending = pendingRef.current;
        if (!pending) return;

        setGateState((s) => ({ ...s, busy: true }));
        try {
            await submitListings(pending.listings, {
                getSubmitBody: pending.getSubmitBody,
                includeLeaseVersion: true,
                signerName,
            });
            closeGate();
            await pending.onSuccess?.();
            pending.resolve();
        } catch (err) {
            setGateState((s) => ({ ...s, busy: false }));
            await pending.onError?.(err);
            pending.resolve();
        }
    }, [closeGate]);

    const handleCancel = useCallback(() => {
        const pending = pendingRef.current;
        closeGate();
        pending?.resolve();
    }, [closeGate]);

    const host = useMemo(() => {
        const firstName = user?.firstName || '';
        const lastName = user?.lastName || '';
        const contactName = [firstName, lastName].filter(Boolean).join(' ') || '—';
        const company = hostData?.company;
        const settings = hostData?.settings;
        return {
            entityName: company?.name || contactName,
            contactName,
            email: settings?.email || '—',
            phone: company?.phoneNumber || settings?.phoneNumber || '—',
            address: formatAddress(
                company?.mailingAddressSameAsPhysical
                    ? company?.physicalAddress
                    : (company?.mailingAddress || company?.physicalAddress)
            ),
            effectiveDate: hostData?.openedAt || '—',
        };
    }, [user, hostData]);

    return {
        runWithLeaseGate,
        gateProps: {
            open: gateState.open,
            listings: gateState.listings,
            leaseVersion: LISTING_LEASE_VERSION,
            busy: gateState.busy,
            host,
            onAccept: handleAccept,
            onCancel: handleCancel,
        },
        LeaseGateModal: LeaseAgreementGate,
    };
}
